import {
    AdditionalDevice,
    BasicCareProcedure,
    ChangeLogEntry,
    ChangeLogModel,
    ChangeLogModelKey,
    ChangeLogModelPayloadMeta,
    ChangeLogOperation,
    Encounter,
    LabValue,
    MedicationAdministration,
    PatientOutput,
    Practitioner,
    Procedure,
    SortOrderEnum,
    VentilationParameter,
    VitalSign,
} from '@mona/models';
import { Logger } from '@mona/shared/logger';
import { groupBy } from '@mona/shared/utils';

const logger = new Logger('PDMS:CHANGE_LOG:apply-changes');

/**
 * FE-only field appended to an optimistically *updated* entity that holds the pre-edit
 * values of the fields the update touched.
 *
 * It exists so a failed/stale optimistic update can be rolled back to the previous value
 * without a BE refetch (slow-BE constraint). Like `hasChanges`/`isSaving`/`isStageRemoved`
 * it lives only in the feature entity store for rendering/revert — it never reaches the
 * backend, because the BE save uses the changelog *payload* (not the entity) and persist
 * just commits drafts server-side.
 */
export interface WithRevertSnapshot {
    revertSnapshot?: Record<string, unknown>;
}

/**
 * Capture the pre-edit values of the keys an optimistic update touches.
 *
 * Preserves an already-captured snapshot across consecutive in-flight edits, so the restore
 * target stays the last value seen *before the first unconfirmed edit* rather than an
 * intermediate optimistic one.
 * @param prevEntity entity as it is right before the payload is merged (the pristine source)
 * @param payload the update payload whose keys are being overwritten
 */
function captureRevertSnapshot(
    prevEntity: AnyObject | undefined,
    payload: AnyObject | undefined,
): Record<string, unknown> | undefined {
    const existing = (prevEntity as WithRevertSnapshot | undefined)?.revertSnapshot;
    if (existing) {
        return existing;
    }
    if (!payload) {
        return undefined;
    }

    const snapshot: Record<string, unknown> = {};
    for (const key of Object.keys(payload)) {
        if (key === 'id') {
            continue;
        }
        snapshot[key] = (prevEntity as AnyObject | undefined)?.[key];
    }
    return snapshot;
}

/**
 * Roll an entity back to its captured {@link WithRevertSnapshot.revertSnapshot} and reset the
 * optimistic flags. `revertSnapshot` is set to `undefined` (not deleted) so an NgRx entity
 * `upsert` — which shallow-merges — actually clears the stale snapshot off the stored entity.
 * @param entity entity carrying an optional `revertSnapshot`
 */
function restoreRevertSnapshot<T extends ChangeLogModel>(entity: T): T {
    const snapshot = (entity as WithRevertSnapshot).revertSnapshot;
    return {
        ...entity,
        ...(snapshot || {}),
        hasChanges: false,
        isSaving: false,
        isStageRemoved: false,
        revertSnapshot: undefined,
    } as unknown as T;
}

/**
 * Applies changes to array of instances
 * @param instances T[] - raw API items
 * @param changes ChangeLogEntry<T>[] - sorted by `createdAt`
 * @param insertInTheBeginning (optional if need to unshift instead of push)
 */
export const applyInstancesChanges = <T extends ChangeLogModel>(
    instances: T[],
    changes: ChangeLogEntry<T>[],
    insertInTheBeginning = false,
): T[] => {
    if (!changes?.length) {
        return instances;
    }

    const changedInstances = [...instances];

    for (const change of changes) {
        if (change.operation === ChangeLogOperation.Create) {
            // create
            const item = {
                ...change.payload,
                id: change.modelId,
                hasChanges: true,
                isSaving: !!change.isSaving,
                lastChangedBy: change.practitionerId,
            } as any;

            const existingIndex = changedInstances.findIndex(ci => ci.id === item.id);
            if (existingIndex !== -1) {
                changedInstances.splice(existingIndex, 1, {
                    ...changedInstances[existingIndex],
                    ...change.payload,
                    hasChanges: true,
                    isSaving: !!change.isSaving,
                    lastChangedBy: change.practitionerId,
                });
                continue;
            }

            if (insertInTheBeginning) {
                changedInstances.unshift(item);
            } else {
                changedInstances.push(item);
            }
        } else if (change.operation === ChangeLogOperation.Update) {
            // update
            const indexToUpdate = changedInstances.findIndex(item => item.id === change.modelId);
            // A stale `Update` change can reference a model that is no longer in
            // the loaded instances list (encounter switch, server-side filter,
            // race with reload). Without this guard `splice(-1, 1, ...)` would
            // corrupt the LAST item in the array — replacing it with a payload-
            // only blob that has no `id`, which then propagates into the entity
            // adapter at key `"undefined"` and eventually emits an Update
            // envelope without `model_id`, producing a BE 400
            // ("model_id: This field is required.").
            if (indexToUpdate === -1) {
                logger.warn('applyInstancesChanges: skipping Update for missing model', {
                    model: change.model,
                    modelId: change.modelId,
                });
                continue;
            }
            changedInstances.splice(indexToUpdate, 1, {
                ...changedInstances[indexToUpdate],
                ...change.payload,
                hasChanges: true,
                isSaving: !!change.isSaving,
                lastChangedBy: change.practitionerId,
                // Snapshot pre-edit value while the update is in flight, so a revert can restore it.
                revertSnapshot: change.isSaving
                    ? captureRevertSnapshot(changedInstances[indexToUpdate], change.payload)
                    : undefined,
            });
        } else if (change.operation === ChangeLogOperation.Delete) {
            // remove
            const indexToRemove = changedInstances.findIndex(item => item.id === change.modelId);
            // Symmetrical guard for Delete (see Update branch above).
            if (indexToRemove === -1) {
                logger.warn('applyInstancesChanges: skipping Delete for missing model', {
                    model: change.model,
                    modelId: change.modelId,
                });
                continue;
            }
            changedInstances.splice(indexToRemove, 1, {
                ...changedInstances[indexToRemove],
                isStageRemoved: true,
                hasChanges: true,
                isSaving: !!change.isSaving,
            });
        }
    }

    return changedInstances;
};

/**
 * Apply single instance change
 * @param instance T
 * @param changes ChangeLogEntry<T>[]
 */
export const applyInstanceChanges = <T extends ChangeLogModel>(instance: T, changes: ChangeLogEntry<T>[]): T => {
    if (!changes?.length) {
        return instance;
    }

    let changedInstance: T = {
        ...instance,
    };

    for (const change of changes) {
        if (change.operation === ChangeLogOperation.Update) {
            changedInstance = {
                ...changedInstance,
                ...change.payload,
            };
        }
    }

    return changedInstance;
};

/**
 * Applies changes to entity map
 * @param entityMap EntityMap<T>
 * @param changes ChangeLogEntry<T>[]
 */
export const applyEntityMapChanges = <T extends ChangeLogModel>(
    entityMap: EntityMap<T>,
    changes: ChangeLogEntry<T>[],
): EntityMap<T> => {
    if (!changes?.length) {
        return entityMap;
    }

    const changedMap = {
        ...entityMap,
    };

    for (const change of changes) {
        if (change.operation === ChangeLogOperation.Create) {
            // create
            const item = {
                ...change.payload,
                id: change.modelId,
                hasChanges: true,
            } as any;
            changedMap[item.id] = item;
        } else if (change.operation === ChangeLogOperation.Update) {
            // update
            changedMap[change.modelId] = {
                ...changedMap[change.modelId],
                ...change.payload,
                hasChanges: true,
            };
        } else if (change.operation === ChangeLogOperation.Delete) {
            // remove
            changedMap[change.modelId] = {
                ...changedMap[change.modelId],
                isStageRemoved: true,
                hasChanges: true,
            };
        }
    }

    return changedMap;
};

/**
 * Applies staged changes to entity grouped map
 * @deprecated
 * @param groupedEntities VitalSignsMap | VentilationParametersMap
 * @param instancesMap EntityMap<VitalSign>
 * @param changes ChangeLogEntry<VitalSign>[]
 * @param groupingField string
 * @param sortingField string
 * @param groupingFieldCallback (value: any, entity: T) => string
 * @param sortOrder 'asc' | 'desc'
 */
export const applyGroupedEntitiesStagedChanges = <
    T extends
        | AdditionalDevice
        | VitalSign
        | VentilationParameter
        | LabValue
        | PatientOutput
        | MedicationAdministration
        | BasicCareProcedure
        | Procedure,
    GM extends AnyObject,
>(
    groupedEntities: GM,
    instancesMap: EntityMap<T>,
    changes: ChangeLogEntry<T>[],
    groupingField = 'code',
    sortingField = 'date',
    groupingFieldCallback?: (value: any, entity: T) => string,
    sortOrder = SortOrderEnum.desc,
): GM => {
    if (!changes?.length || !groupedEntities) {
        return groupedEntities;
    }

    // Result updated grouped map
    const updatedGroupedMap: GM = {
        ...groupedEntities,
    };

    // Updated instances map as helper for applying changes on newly created items
    const newlyCreatedItemsMap: EntityMap<T> = {};

    /**
     * For more efficiency we save types which should be re-sorted
     * Because we have create in past
     */
    const typesWithNewEntries: Set<string> = new Set();

    // Applying changes
    for (const change of changes) {
        if (change.operation === ChangeLogOperation.Create) {
            // create
            handleInstanceCreate(
                change,
                newlyCreatedItemsMap,
                updatedGroupedMap,
                typesWithNewEntries,
                groupingField,
                groupingFieldCallback,
            );
        } else {
            // update and delete
            handleInstanceUpdateDelete(
                change,
                instancesMap,
                newlyCreatedItemsMap,
                updatedGroupedMap,
                groupingField,
                groupingFieldCallback,
            );
        }
    }

    // Sorting entities that have new entries
    if (sortingField) {
        sortChangedEntries(updatedGroupedMap, typesWithNewEntries, sortingField, sortOrder);
    }

    return updatedGroupedMap;
};

/**
 * Contains shared logic for vital signs and lab values create
 * @deprecated
 * @param change ChangeLogEntry<T>
 * @param newlyCreatedItemsMap EntityMap<T>
 * @param updatedGroupedMap VitalSignsMap | LabValuesMap
 * @param typesWithNewEntries Set<string>
 * @param groupingField string
 * @param groupingFieldCallback (value: any, entity: T) => string,
 */
const handleInstanceCreate = <
    T extends
        | AdditionalDevice
        | LabValue
        | VitalSign
        | VentilationParameter
        | PatientOutput
        | MedicationAdministration
        | BasicCareProcedure
        | Procedure,
>(
    change: ChangeLogEntry<T>,
    newlyCreatedItemsMap: EntityMap<T>,
    updatedGroupedMap: AnyObject,
    typesWithNewEntries: Set<string>,
    groupingField = 'code',
    groupingFieldCallback?: (value: any, entity: T) => string,
): void => {
    const newEntry = {
        ...change.payload,
        id: change.modelId,
        hasChanges: true,
        isSaving: !!change.isSaving,
    } as T;

    // Handle key
    const key = groupingFieldCallback
        ? groupingFieldCallback(newEntry[groupingField], newEntry)
        : newEntry[groupingField];
    const existingTypeEntries = updatedGroupedMap[key];

    const existingEntries = [...(existingTypeEntries || [])];
    const existingIndex = existingEntries.findIndex((item: any) => item.id === change.modelId);
    if (existingIndex !== -1) {
        existingEntries.splice(existingIndex, 1, {
            ...existingEntries[existingIndex],
            ...change.payload,
            hasChanges: true,
            isSaving: !!change.isSaving,
        });
        updatedGroupedMap[key] = existingEntries as any[];
    } else {
        updatedGroupedMap[key] = [...existingEntries, newEntry] as any[];
        typesWithNewEntries.add(key);
    }

    // Adds item to newly created items map
    newlyCreatedItemsMap[newEntry.id] = newEntry;
};

/**
 * Contains shared logic for vital signs and lab values update / delete logic
 * @deprecated
 * @param change ChangeLogEntry<T>
 * @param instancesMap EntityMap<T>
 * @param newlyCreatedItemsMap EntityMap<T>
 * @param updatedGroupedMap VitalSignsMap | LabValuesMap
 * @param groupingField string
 * @param groupingFieldCallback (value: any, entity: T) => string,
 */
const handleInstanceUpdateDelete = <
    T extends
        | AdditionalDevice
        | LabValue
        | VitalSign
        | VentilationParameter
        | PatientOutput
        | MedicationAdministration
        | BasicCareProcedure
        | Procedure,
>(
    change: ChangeLogEntry<T>,
    instancesMap: EntityMap<T>,
    newlyCreatedItemsMap: EntityMap<T>,
    updatedGroupedMap: AnyObject,
    groupingField = 'code',
    groupingFieldCallback?: (value: any, entity: T) => string,
): void => {
    const updatedItem = instancesMap[change.modelId] || newlyCreatedItemsMap[change.modelId];
    // Handle key
    const key = groupingFieldCallback
        ? groupingFieldCallback(updatedItem?.[groupingField], updatedItem)
        : updatedItem?.[groupingField];

    if (!updatedItem) {
        return;
    }

    const updatedExistingTypeEntries = [...(updatedGroupedMap[key] || [])];
    const indexToUpdate = updatedExistingTypeEntries.findIndex(item => item.id === change.modelId);

    if (change.operation === ChangeLogOperation.Update) {
        updatedExistingTypeEntries.splice(indexToUpdate, 1, {
            ...updatedExistingTypeEntries[indexToUpdate],
            ...change.payload,
            hasChanges: true,
            isSaving: !!change.isSaving,
        });
    } else if (change.operation === ChangeLogOperation.Delete && indexToUpdate > -1) {
        updatedExistingTypeEntries.splice(indexToUpdate, 1, {
            ...updatedExistingTypeEntries[indexToUpdate],
            isStageRemoved: true,
            hasChanges: true,
            isSaving: !!change.isSaving,
        });
    }

    updatedGroupedMap[key] = updatedExistingTypeEntries as any[]; // T[]
};

/**
 * Contains shared sort logic for new entries of vital signs and lab values
 * @deprecated
 * @param updatedGroupedMap VitalSignsMap | LabValuesMap
 * @param typesWithNewEntries Set<string>
 * @param sortingField sortingField
 * @param sortOrder asc | desc
 */
const sortChangedEntries = (
    updatedGroupedMap: AnyObject,
    typesWithNewEntries: Set<string>,
    sortingField = 'date',
    sortOrder: SortOrderEnum,
): void => {
    typesWithNewEntries.forEach(type => {
        updatedGroupedMap[type] = [...updatedGroupedMap[type]].sort((prev, next) => {
            if (sortOrder === SortOrderEnum.asc) {
                return next[sortingField].getTime() - prev[sortingField].getTime();
            } else {
                return prev[sortingField].getTime() - next[sortingField].getTime();
            }
        }) as any[]; // T[]
    });
};

/**
 * Merge data with persisted changes
 *
 * Changes array is reversed here, because e.g. we have 3 changes for same item id - CREATE, UPDATE, DELETE
 * most likely, for the same id, DELETE operation is last - by design user cannot edit afer delete
 * so we need to skip item from adding to `toUpdate` array if it exists (see test)
 * @param data
 * @param changes
 * @returns data array with merged changes into it
 */
export function mergeDataWithPersistedChanges<T extends ChangeLogModel, C extends ChangeLogEntry>(
    data: T[],
    changes: C[],
): T[] {
    changes = mergeChangesOnModelId(changes) as any;

    for (let i = changes.length - 1; i >= 0; i--) {
        const change = changes[i];
        const dataItemIdx = data.findIndex((item: any) => item.id === change.modelId);

        if (change.operation === ChangeLogOperation.Delete) {
            dataItemIdx !== -1 && data.splice(dataItemIdx, 1);
        } else {
            if (dataItemIdx !== -1) {
                data[dataItemIdx] = { ...data[dataItemIdx], ...change.payload };
            } else {
                data.push({
                    ...data[dataItemIdx],
                    ...change.payload,
                    id: change.modelId,
                } as any);
            }
        }
    }

    return data;
}

/**
 * Get persisted changes data split by operation
 *
 * Changes array is reversed here, because e.g. we have 3 changes for same item id - CREATE, UPDATE, DELETE
 * most likely, for the same id, DELETE operation is last - by design user cannot edit afer delete
 * so we need to skip item from adding to `toUpdate` array if it exists (see test)
 * @param data
 * @param changes
 * @returns object with toRemove and toUpdate arrays
 */
export function getPersistedChangesData<T extends ChangeLogModel, C extends ChangeLogEntry>(
    data: T[],
    changes: C[],
): { toRemove: T[]; toUpdate: T[] } {
    const toRemove: T[] = [];
    const toUpdate: T[] = [];

    const processedIds = new Set<string>();

    for (let i = changes.length - 1; i >= 0; i--) {
        const { modelId: id, payload, operation } = changes[i];

        if (processedIds.has(id)) {
            continue;
        }

        if (operation !== ChangeLogOperation.Create) {
            processedIds.add(id);
        }

        const foundDataItem = data.find((item: any) => item.id === id) || {};
        const newItem: T = { ...foundDataItem, ...payload, id } as any;

        (newItem as any).hasChanges = false;

        if (operation === ChangeLogOperation.Create) {
            data.push(newItem);
        }

        if (operation === ChangeLogOperation.Delete) {
            toRemove.push(newItem);
        } else {
            toUpdate.push(newItem);
        }
    }

    return {
        toRemove,
        toUpdate,
    };
}

type RevertChangeLogAction = {
    type: string;
    change?: ChangeLogEntry<ChangeLogModel>;
    changes?: ChangeLogEntry<ChangeLogModel>[];
};

/**
 * Extract reverted changelog entries for a model from a revert / stale-sweep action.
 * @param action
 * @param model
 */
export function getRevertedChangesForModel(
    action: RevertChangeLogAction,
    model: ChangeLogModelKey,
): ChangeLogEntry<ChangeLogModel>[] {
    if (action.type === 'ENCOUNTER:REVERT_OPTIMISTIC_CHANGE' && action.change?.model === model) {
        return [action.change];
    }

    if (
        (action.type === 'ENCOUNTER:REVERT_OPTIMISTIC_CHANGES' ||
            action.type === 'ENCOUNTER:REVERT_STALE_SAVING_ENTRIES') &&
        action.changes
    ) {
        return action.changes.filter(change => change.model === model);
    }

    return [];
}

/**
 * Ids of reverted optimistic *creates* that must be removed from an entity-adapter store.
 *
 * The upsert-based effects can re-project updates/deletes but cannot remove a row, so a
 * reverted create has to be deleted explicitly (dispatch the feature's `remove…` action).
 *
 * Derived purely from the reverted changes (the revert action payload) — deliberately
 * independent of the live `changesMap`, so removal never depends on reducer-vs-effect
 * ordering. A reverted create has a unique id and cannot also be a pending change, so no
 * cross-check against remaining changes is needed.
 * @param revertedChanges
 */
export function getRevertedCreateIds(revertedChanges: ChangeLogEntry<ChangeLogModel>[]): string[] {
    return revertedChanges
        .filter(change => change.operation === ChangeLogOperation.Create)
        .map(change => change.modelId);
}

/**
 * Clear optimistic flags on entities whose pending save was reverted or swept as stale.
 * @param data
 * @param revertedChanges
 * @param remainingChanges
 */
export function getClearedRevertedEntities<T extends ChangeLogModel>(
    data: T[],
    revertedChanges: ChangeLogEntry<ChangeLogModel>[],
    remainingChanges: ChangeLogEntry<ChangeLogModel>[],
): T[] {
    if (!revertedChanges.length) {
        return [];
    }

    const stillPendingIds = new Set(remainingChanges.map(change => change.modelId));
    const cleared: T[] = [];

    for (const change of revertedChanges) {
        if (stillPendingIds.has(change.modelId) || change.operation === ChangeLogOperation.Create) {
            continue;
        }

        const found = data.find(item => item.id === change.modelId);
        if (found) {
            // Roll a reverted update back to its captured pre-edit value (no-op for deletes,
            // which carry no snapshot — they're just un-staged) and clear optimistic flags.
            cleared.push(restoreRevertSnapshot(found));
        }
    }

    return cleared;
}

/**
 * Build entity upserts from remaining changelog entries plus cleared reverted entities.
 * @param data
 * @param remainingChanges
 * @param revertedChanges
 */
export function reconcileEntityUpdatesWithChanges<T extends ChangeLogModel>(
    data: T[],
    remainingChanges: ChangeLogEntry<ChangeLogModel>[],
    revertedChanges: ChangeLogEntry<ChangeLogModel>[] = [],
): T[] {
    const pendingUpdates = remainingChanges.length ? getChangesData(data, remainingChanges) : [];
    const cleared = getClearedRevertedEntities(data, revertedChanges, remainingChanges);

    // A reverted create is removed from the store by the effect (remove… action). Make sure
    // we never re-project it back into the upsert payload, otherwise it would reappear with
    // its optimistic isSaving flag still set (the "stays disabled" symptom).
    const revertedCreateIds = new Set(getRevertedCreateIds(revertedChanges));

    return [...pendingUpdates, ...cleared].filter(item => !revertedCreateIds.has(item.id));
}

/**
 * Reconcile a full instances array after revert / stale-sweep actions.
 * @param instances
 * @param remainingChanges
 * @param revertedChanges
 */
export function reconcileInstancesWithChanges<T extends ChangeLogModel>(
    instances: T[],
    remainingChanges: ChangeLogEntry<ChangeLogModel>[],
    revertedChanges: ChangeLogEntry<ChangeLogModel>[] = [],
): T[] | null {
    if (!remainingChanges.length && !revertedChanges.length) {
        return null;
    }

    const stillPendingIds = new Set(remainingChanges.map(change => change.modelId));
    const revertedCreateIds = new Set(
        revertedChanges
            .filter(change => !stillPendingIds.has(change.modelId) && change.operation === ChangeLogOperation.Create)
            .map(change => change.modelId),
    );
    const revertedOtherIds = new Set(
        revertedChanges
            .filter(change => !stillPendingIds.has(change.modelId) && change.operation !== ChangeLogOperation.Create)
            .map(change => change.modelId),
    );

    let result = remainingChanges.length
        ? applyInstancesChanges(instances, remainingChanges as ChangeLogEntry<T>[])
        : [...instances];

    if (revertedCreateIds.size) {
        result = result.filter(item => !revertedCreateIds.has(item.id));
    }

    if (revertedOtherIds.size) {
        result = result.map(item => (revertedOtherIds.has(item.id) ? restoreRevertSnapshot(item) : item));
    }

    return result;
}

/**
 * get changes data
 * @param data
 * @param changes
 * @returns T[]
 */
export function getChangesData<T extends ChangeLogModel, C extends ChangeLogEntry>(data: T[], changes: C[]): T[] {
    const toUpdate: T[] = [];

    const processedIds = new Set<string>();

    for (let i = changes.length - 1; i >= 0; i--) {
        const { modelId: id, payload, operation, isSaving } = changes[i];

        if (processedIds.has(id)) {
            continue;
        }

        processedIds.add(id);

        const foundDataItem = data.find((item: any) => item.id === id) || {};

        // `id` (the changelog `modelId`) must win over any `id` carried in the payload —
        // some create callers spread a form value that still has `id: null` (e.g. the
        // lab-values cell editor), which would otherwise key the optimistic entity under
        // `null` instead of `modelId`. That mismatch makes the later `remove…([modelId])`
        // on a revert miss the row, leaving a stale saving cell on screen. Keep `id` last.
        const newItem: T & {
            hasChanges: boolean;
            isSaving?: boolean;
            isStageRemoved?: boolean;
            revertSnapshot?: Record<string, unknown>;
        } = {
            ...foundDataItem,
            ...payload,
            id,
        } as any;

        newItem.hasChanges = true;
        newItem.isSaving = !!isSaving;
        if (operation === ChangeLogOperation.Delete) {
            newItem.isStageRemoved = true;
        }

        // Snapshot the pre-edit value only while an *update* is in flight, so a later revert
        // can restore it. Cleared (undefined) otherwise — including on success, where the
        // confirmed entry is re-projected with isSaving:false (see reduceSaveChangeSucceeded).
        newItem.revertSnapshot =
            operation === ChangeLogOperation.Update && isSaving
                ? captureRevertSnapshot(foundDataItem, payload)
                : undefined;

        toUpdate.push(newItem);
    }

    return toUpdate;
}

/**
 * Extend payload with meta data
 * @param payload
 * @param encounter
 * @param practitioner
 * @param addMilliseconds
 */
export function extendPayloadWithMetaData(
    payload: ChangeLogEntry<ChangeLogModel>['payload'],
    encounter: Encounter,
    practitioner: Practitioner,
    addMilliseconds?: number,
): ChangeLogEntry<ChangeLogModel>['payload'] & ChangeLogModelPayloadMeta {
    const lastChangedAt = new Date();
    lastChangedAt.setMilliseconds(addMilliseconds || 0);

    const extendedPayload = {
        ...payload,
        encounterId: encounter?.id,
        patientId: encounter?.patient?.id,
        lastChangedBy: practitioner?.id,
        lastChangedAt,
    };

    // additionaly check if responsibleNurseId should be filled with current user id
    if (payload?.responsibleNurseId) {
        extendedPayload['responsibleNurseId'] = practitioner?.id;
    }

    return extendedPayload;
}

/**
 * merge changes on modelId
 * @param changes
 */
export function mergeChangesOnModelId(changes: ChangeLogEntry[]): ChangeLogEntry[] {
    if (!changes?.length) {
        return [];
    }

    const groupedByModelId = groupBy(changes, ch => ch.modelId, false, false);
    const mergedChanges: ChangeLogEntry[] = [];

    for (const key in groupedByModelId) {
        mergedChanges.push(
            groupedByModelId[key].reduce(
                (res, change) => {
                    res.operation = change.operation;
                    res.payload = { ...res.payload, ...change.payload };
                    return res;
                },
                { ...groupedByModelId[key][0] },
            ),
        );
    }

    return mergedChanges;
}
